import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, Calendar, Check, X, User, ArrowRight, Clock, Zap, Leaf, RefreshCw } from 'lucide-react';

const DeliveryNegotiation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const orderData = location.state || {};
  const [decision, setDecision] = useState(null); // 'approved', 'reassign'
  const [isLoading, setIsLoading] = useState(false);

  // Demo data
  // No demo fallbacks: only render a real proposal passed in by the caller.
  const order = {
    id: orderData.orderId,
    product: orderData.product || '—',
    tailorName: orderData.tailorName || 'Your tailor',
    originalDate: orderData.originalDate,
    proposedDate: orderData.proposedDate,
    deliveryType: orderData.deliveryType || 'standard',
    amount: orderData.amount || '—',
  };
  const hasProposal = Boolean(order.id && order.originalDate && order.proposedDate);

  const getDeliveryTypeInfo = (type) => {
    switch (type) {
      case 'urgent': return { label: 'Urgent', icon: Zap, color: '#991B1B' };
      case 'flexible': return { label: 'Flexible', icon: Leaf, color: '#166534' };
      default: return { label: 'Standard', icon: Clock, color: '#D4AF37' };
    }
  };

  const deliveryInfo = getDeliveryTypeInfo(order.deliveryType);
  const DeliveryIcon = deliveryInfo.icon;

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  const daysDiff = Math.ceil((new Date(order.proposedDate) - new Date(order.originalDate)) / (1000 * 60 * 60 * 24));

  const handleApprove = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setDecision('approved');
    }, 1200);
  };

  const handleReassign = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setDecision('reassign');
    }, 1200);
  };

  if (!hasProposal) {
    return (
      <div data-testid="negotiation-not-found" className="min-h-screen flex flex-col items-center justify-center px-6 text-center bg-[#FAF9F6]">
        <p className="text-lg mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>No delivery change to review</p>
        <p className="text-[12px] mb-5" style={{ color: '#595959' }}>There is no pending delivery-date proposal for this order.</p>
        <button data-testid="negotiation-my-orders-btn" onClick={() => navigate('/my-orders')}
          className="px-6 py-3 text-[11px] tracking-[0.18em] uppercase text-white"
          style={{ background: 'linear-gradient(135deg, #1A1A1A 0%, #D4AF37 140%)' }}>
          Go to My Orders
        </button>
      </div>
    );
  }

  return (
    <div 
      data-testid="delivery-negotiation"
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: '#FAF9F6', fontFamily: 'Outfit, sans-serif' }}
    >
      {/* Header */}
      <div 
        className="sticky top-0 z-50 flex items-center justify-between px-4 py-4 border-b"
        style={{ backgroundColor: 'rgba(250, 249, 246, 0.9)', backdropFilter: 'blur(12px)', borderColor: '#E5E5E5' }}
      >
        <button data-testid="back-btn" onClick={() => navigate(-1)} className="p-2 -ml-2">
          <ChevronLeft className="w-6 h-6" style={{ color: '#0F0F0F' }} strokeWidth={1.5} />
        </button>
        <h1 className="text-lg" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>Delivery Update</h1>
        <div className="w-10" />
      </div>

      <div className="flex-1 px-6 py-8">
        {!decision ? (
          <>
            {/* Tailor Info */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)' }}>
                <User className="w-8 h-8 text-white" strokeWidth={1.5} />
              </div>
              <h2 className="text-2xl mb-1" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>
                Date Change Request
              </h2>
              <p className="text-sm" style={{ color: '#595959' }}>
                {order.tailorName} has proposed a new delivery date
              </p>
            </div>

            {/* Order Info */}
            <div className="bg-white border p-4 mb-6" style={{ borderColor: '#E5E5E5' }}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-medium" style={{ color: '#0F0F0F' }}>{order.product}</p>
                  <p className="text-xs" style={{ color: '#595959' }}>{order.id}</p>
                </div>
                <div className="flex items-center gap-1 px-2 py-1" style={{ backgroundColor: deliveryInfo.color + '14' }}>
                  <DeliveryIcon className="w-3.5 h-3.5" style={{ color: deliveryInfo.color }} strokeWidth={1.5} />
                  <span className="text-[11px]" style={{ color: deliveryInfo.color }}>{deliveryInfo.label}</span>
                </div>
              </div>
              <div className="flex justify-between text-sm">
                <span style={{ color: '#595959' }}>Amount</span>
                <span className="font-medium" style={{ color: '#0F0F0F' }}>{order.amount}</span>
              </div>
            </div>

            {/* Date Comparison */}
            <div className="mb-8">
              <div className="flex items-center gap-4">
                {/* Original Date */}
                <div className="flex-1 p-4 border text-center" style={{ borderColor: '#E5E5E5' }}>
                  <p className="text-[10px] tracking-[0.15em] uppercase mb-2" style={{ color: '#595959' }}>Your Date</p>
                  <Calendar className="w-5 h-5 mx-auto mb-2" style={{ color: '#595959' }} strokeWidth={1.5} />
                  <p className="text-sm font-medium" style={{ color: '#0F0F0F' }}>{formatDate(order.originalDate)}</p>
                </div>

                <ArrowRight className="w-5 h-5 flex-shrink-0" style={{ color: '#D4AF37' }} strokeWidth={1.5} />

                {/* Proposed Date */}
                <div className="flex-1 p-4 border text-center" style={{ borderColor: '#D4AF37', backgroundColor: 'rgba(212,175,55,0.03)' }}>
                  <p className="text-[10px] tracking-[0.15em] uppercase mb-2" style={{ color: '#D4AF37' }}>Tailor's Date</p>
                  <Calendar className="w-5 h-5 mx-auto mb-2" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
                  <p className="text-sm font-medium" style={{ color: '#0F0F0F' }}>{formatDate(order.proposedDate)}</p>
                </div>
              </div>
              <p className="text-center text-xs mt-3" style={{ color: '#595959' }}>
                {daysDiff > 0 ? `${daysDiff} day${daysDiff > 1 ? 's' : ''} later than requested` : `${Math.abs(daysDiff)} day${Math.abs(daysDiff) > 1 ? 's' : ''} earlier`}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                data-testid="approve-date-btn"
                onClick={handleApprove}
                disabled={isLoading}
                className="w-full py-4 text-white text-sm tracking-[0.1em] uppercase flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)', fontWeight: 500 }}
              >
                <Check className="w-4 h-4" strokeWidth={2} />
                {isLoading ? 'Processing...' : 'Accept New Date'}
              </button>
              <button
                data-testid="reassign-tailor-btn"
                onClick={handleReassign}
                disabled={isLoading}
                className="w-full py-4 text-sm tracking-[0.1em] uppercase flex items-center justify-center gap-2 border disabled:opacity-50"
                style={{ borderColor: '#E5E5E5', color: '#595959', fontWeight: 500 }}
              >
                <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
                Choose Another Tailor
              </button>
            </div>
          </>
        ) : decision === 'approved' ? (
          /* Approved Confirmation */
          <div className="flex-1 flex flex-col items-center justify-center text-center py-16">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)' }}>
              <Check className="w-10 h-10 text-white" strokeWidth={2} />
            </div>
            <h2 className="text-2xl mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>Date Confirmed</h2>
            <p className="text-sm mb-2" style={{ color: '#595959' }}>
              New delivery date: <span style={{ color: '#0F0F0F', fontWeight: 500 }}>{formatDate(order.proposedDate)}</span>
            </p>
            <p className="text-xs mb-8" style={{ color: '#595959' }}>
              {order.tailorName} will begin working on your order
            </p>
            <button
              data-testid="go-to-tracking-btn"
              onClick={() => navigate('/order-tracking', { state: { orderId: orderData.orderId } })}
              className="py-3 px-8 text-sm tracking-[0.1em] uppercase text-white"
              style={{ background: 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)', fontWeight: 500 }}
            >
              Track Order
            </button>
          </div>
        ) : (
          /* Reassign Confirmation */
          <div className="flex-1 flex flex-col items-center justify-center text-center py-16">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: 'rgba(212,175,55,0.08)' }}>
              <RefreshCw className="w-10 h-10" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>Finding New Tailor</h2>
            <p className="text-sm mb-2" style={{ color: '#595959' }}>
              We're assigning a new tailor who can deliver by
            </p>
            <p className="text-sm mb-8" style={{ color: '#0F0F0F', fontWeight: 500 }}>
              {formatDate(order.originalDate)}
            </p>
            <button
              data-testid="back-to-home-btn"
              onClick={() => navigate('/landing')}
              className="py-3 px-8 text-sm tracking-[0.1em] uppercase"
              style={{ backgroundColor: '#0F0F0F', color: '#FFFFFF', fontWeight: 500 }}
            >
              Back to Home
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryNegotiation;
